import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import {
    getBookingsByPoojari,
    confirmBooking,
    poojariMarkComplete,
    rejectBooking,
    poojariCancelBooking
} from '../../services/bookingService';
import { formatPaise } from '../../utils/money';
import {
    createReview,
    getReviewsByUser,
    getUserRating
} from '../../services/reviewService';
import { getUserById } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    const keys = ['data', 'content', 'result', 'items'];
    for (const k of keys) {
        if (Array.isArray(payload[k])) return payload[k];
    }
    return [];
};

const PoojariBookings = () => {
    const location = useLocation();
    const [filter, setFilter] = useState(
        location.state?.filter || 'PENDING');

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionId, setActionId] = useState(null);

    // Users ki rating cache - baar baar API na ho
    const [userRatings, setUserRatings] = useState({});
    const [userNames, setUserNames] = useState({});

    // Reject modal
    const [rejectId, setRejectId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejecting, setRejecting] = useState(false);
    // Poojari cancel modal (accept ke baad)
    // ⚠️ Reject wali state se ALAG - dono modal ek saath khul sakte
    // the agar ek hi state share karte, aur fee wala message galat
    // booking pe dikh jaata.
    const [cancelBooking, setCancelBooking] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    // Cash received modal
    const [cashBooking, setCashBooking] = useState(null);
    const [savingCash, setSavingCash] = useState(false);

    // Review modal
    const [reviewBooking, setReviewBooking] = useState(null);
    const [reviewForm, setReviewForm] = useState({
        rating: 5,
        title: '',
        comment: ''
    });
    const [savingReview, setSavingReview] = useState(false);
    const [reviewedIds, setReviewedIds] = useState([]);

    const { token, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/poojari-login');
            return;
        }
        const timer = setTimeout(() => {
            fetchBookings();
            fetchMyReviews();
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    const fetchBookings = async () => {
        setLoading(true);
        try {
            const poojariId = user?.id;
            if (!poojariId) {
                toast.error('Session expired. Login again.');
                navigate('/poojari-login');
                return;
            }
            const raw = await getBookingsByPoojari(poojariId);
            const list = normalizeList(raw);
            setBookings(list);
            loadUserInfo(list);
        } catch (error) {
            toast.error('Error fetching bookings');
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };
    useLiveRefresh(fetchBookings);

    // Har user ki rating aur naam le aao
    const loadUserInfo = async (list) => {
        const ids = [...new Set(list.map(b => b.userId))];

        for (const id of ids) {
            try {
                const [rating, info] = await Promise.all([
                    getUserRating(id),
                    getUserById(id)
                ]);
                setUserRatings(prev => ({
                    ...prev, [id]: rating
                }));
                setUserNames(prev => ({
                    ...prev,
                    [id]: `${info.firstName} ${info.lastName}`
                }));
            } catch (err) {
                console.log('Could not load user', id);
            }
        }
    };

    // Kaunsi bookings ka review de chuka hoon
    const fetchMyReviews = async () => {
        try {
            const poojariId = user?.id;
            if (!poojariId) return;
            const data = await getReviewsByUser(poojariId);
            const ids = normalizeList(data)
                .filter(r =>
                    r.reviewType === 'POOJARI_TO_USER')
                .map(r => r.bookingId)
                .filter(Boolean);
            setReviewedIds(ids);
        } catch (error) {
            setReviewedIds([]);
        }
    };

    const handleConfirm = async (id) => {
        setActionId(id);
        try {
            await confirmBooking(id);
            toast.success('Booking confirmed');
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not confirm booking');
        } finally {
            setActionId(null);
        }
    };

    /**
     * Poojari ne "Complete" dabaya.
     *
     * ⚠️ Ab do raste hain:
     *
     * 1. Paisa BAAKI hai (remainingAmount > 0)
     *    -> popup dikhao "Kya ₹X mil gaye?"
     *    -> Haan dabaye tabhi backend call hoga
     *
     * 2. Paisa poora aa chuka hai
     *    -> seedha backend call
     *
     * Purana completeBooking() payment check hi nahi karta tha -
     * poojari "Done" dabata aur booking COMPLETED ho jaati chahe
     * paisa aaya ho ya nahi.
     */
    const handleComplete = async (booking) => {
        const remaining = booking.remainingAmount || 0;

        // Paisa baaki hai to pehle popup - wahin se call hoga
        if (remaining > 0) {
            setCashBooking(booking);
            return;
        }

        // Paisa poora aa chuka hai - seedha complete
        setActionId(booking.id);
        try {
            await poojariMarkComplete(booking.id, false);
            toast.success('Pooja marked as completed');
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not complete booking');
        } finally {
            setActionId(null);
        }
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            return toast.warning('Please enter a reason');
        }
        setRejecting(true);
        try {
            await rejectBooking(rejectId, rejectReason);
            toast.success('Booking rejected');
            setRejectId(null);
            setRejectReason('');
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not reject booking');
        } finally {
            setRejecting(false);
        }
    };

    /**
     * Pooja mein kitne ghante bache hain. Null = date/time hi nahi.
     *
     * ⚠️ Cancel ka button dikhana-chhupana isi se hota hai. Asli rok
     * BACKEND mein hai (24 ghante ke andar wo mana kar deta hai) -
     * ye sirf itna hai ki poojari reason likhne ke BAAD mana na
     * suney. Frontend ka check kabhi suraksha nahi hota.
     *
     * ⚠️ `${date}T${time}` seedha Date mein chala jaata hai, kyunki
     * backend ISO format bhejta hai.
     */
    const hoursUntilPooja = (b) => {
        if (!b.poojaDate || !b.poojaTime) {
            return null;
        }
        const at = new Date(`${b.poojaDate}T${b.poojaTime}`);
        return (at - new Date()) / (1000 * 60 * 60);
    };

    /**
     * Poojari accept karne ke BAAD cancel kar raha hai.
     *
     * ⚠️ Reject se alag - usme kisi ka nuksaan nahi hota. Yahan
     * user ko poora refund jaata hai aur POOJARI PAR FEE lagti hai.
     * Isliye popup mein fee saaf dikhai jaati hai, aur confirm
     * karne ke baad hi ye chalta hai.
     *
     * ⚠️ Pooja se 24 ghante ke andar backend mana kar dega - wahi
     * message user ko dikha dete hain.
     */
    const confirmPoojariCancel = async () => {
        if (!cancelReason.trim()) {
            return toast.warning('Please enter a reason');
        }
        setCancelling(true);
        try {
            await poojariCancelBooking(cancelBooking.id, cancelReason);
            toast.success('Booking cancelled - the user has been refunded');
            setCancelBooking(null);
            setCancelReason('');
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not cancel booking');
        } finally {
            setCancelling(false);
        }
    };

    /**
     * Popup mein "Haan, paisa mil gaya" dabaya.
     *
     * ⚠️ cashReceived = true bhejte hain. Backend ye karta hai:
     *   - paidAmount = poora amount
     *   - paymentStatus = COMPLETED
     *   - poojariConfirmedAt set
     *
     * Aur agar user pehle hi confirm kar chuka ho, to booking
     * turant RELEASE ho jaati hai (commission calculate hoke).
     */
    const confirmCash = async () => {
        if (!cashBooking) return;
        setSavingCash(true);
        try {
            await poojariMarkComplete(cashBooking.id, true);
            toast.success('Payment received and pooja completed');
            setCashBooking(null);
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not record payment');
        } finally {
            setSavingCash(false);
        }
    };

    // ===== Review =====

    const openReviewModal = (b) => {
        setReviewBooking(b);
        setReviewForm({ rating: 5, title: '', comment: '' });
    };

    const confirmReview = async (e) => {
        e.preventDefault();
        if (!reviewForm.title.trim()) {
            return toast.warning('Please add a title');
        }
        setSavingReview(true);
        try {
            await createReview({
                userId: reviewBooking.userId,
                poojariId: reviewBooking.poojariId,
                bookingId: reviewBooking.id,
                reviewType: 'POOJARI_TO_USER',
                rating: parseInt(reviewForm.rating),
                title: reviewForm.title,
                comment: reviewForm.comment
            });
            toast.success('Thank you for your feedback');
            setReviewedIds([...reviewedIds,
            reviewBooking.id]);
            setReviewBooking(null);
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not submit review');
            fetchMyReviews();
        } finally {
            setSavingReview(false);
        }
    };

    const statusColor = (status) => {
        const map = {
            PENDING: { bg: '#FFF4E0', text: '#8A5300' },
            CONFIRMED: { bg: '#E6F1FB', text: '#0C447C' },
            COMPLETED: { bg: '#EAF3DE', text: '#27500A' },
            CANCELLED: { bg: '#FCEBEB', text: '#791F1F' },
            REJECTED: { bg: '#EFEFEF', text: '#444' }
        };
        return map[status] || { bg: '#EFEFEF', text: '#444' };
    };

    const paymentModeLabel = (mode) => {
        if (mode === 'PAY_AFTER_POOJA')
            return 'Cash after pooja';
        if (mode === 'FULL_ADVANCE') return 'Full advance';
        if (mode === 'PARTIAL_ADVANCE')
            return 'Token advance';
        return mode || '-';
    };

    const filteredBookings = filter === 'ALL'
        ? bookings
        : bookings.filter(b => b.bookingStatus === filter);

    const pendingCount = bookings.filter(b =>
        b.bookingStatus === 'PENDING').length;

    // Kitni bookings ka paisa atka hai
    const unpaidCount = bookings.filter(b =>
        b.bookingStatus === 'COMPLETED'
        && (b.remainingAmount || 0) > 0).length;

    const filters = [
        {
            key: 'PENDING', label: 'Pending',
            count: pendingCount
        },
        {
            key: 'CONFIRMED', label: 'Confirmed',
            count: null
        },
        {
            key: 'COMPLETED', label: 'Completed',
            count: unpaidCount
        },
        { key: 'ALL', label: 'All', count: null }
    ];

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: RED }}></div>
        </div>
    );

    return (
        <div style={{ backgroundColor: '#FFF5F5' }}
            className="pb-screen">

            {/* ===== HEADER ===== */}
            <div style={{
                backgroundColor: DARK_RED,
                padding: '14px 16px'
            }}>
                <p className="mb-0 fw-bold"
                    style={{ color: GOLD, fontSize: '15px' }}>
                    📅 My Bookings
                </p>
                <p className="mb-0"
                    style={{
                        color: '#FFE4B5',
                        fontSize: '12px'
                    }}>
                    {pendingCount > 0
                        ? `${pendingCount} booking(s) need your response`
                        : unpaidCount > 0
                            ? `${unpaidCount} payment(s) pending`
                            : 'All caught up'}
                </p>
            </div>

            <div className="container"
                style={{ maxWidth: '640px' }}>
                <div className="py-3">

                    {/* Filter tabs */}
                    <div className="d-flex gap-1 p-1 mb-3"
                        style={{
                            backgroundColor: 'white',
                            border: `1px solid ${GOLD}`,
                            borderRadius: '12px',
                            overflowX: 'auto'
                        }}>
                        {filters.map(f => (
                            <button
                                key={f.key}
                                onClick={() => setFilter(f.key)}
                                className="flex-fill border-0 fw-bold"
                                style={{
                                    padding: '10px 6px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                    backgroundColor:
                                        filter === f.key
                                            ? RED : 'transparent',
                                    color: filter === f.key
                                        ? GOLD : '#666'
                                }}>
                                {f.label}
                                {f.count > 0 && (
                                    <span className="badge ms-1"
                                        style={{
                                            backgroundColor: GOLD,
                                            color: DARK_RED,
                                            fontSize: '10px'
                                        }}>
                                        {f.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {filteredBookings.length === 0 ? (
                        <div className="text-center py-5">
                            <h6 style={{ color: DARK_RED }}>
                                No {filter.toLowerCase()} bookings
                            </h6>
                            <p className="text-muted"
                                style={{ fontSize: '14px' }}>
                                Bookings will appear here
                            </p>
                        </div>
                    ) : (
                        <div className="row g-3">
                            {filteredBookings.map(b => {
                                const sc = statusColor(
                                    b.bookingStatus);
                                const busy = actionId === b.id;
                                const rating =
                                    userRatings[b.userId];
                                const name =
                                    userNames[b.userId];
                                const reviewed =
                                    reviewedIds.includes(b.id);
                                const unpaid =
                                    (b.remainingAmount || 0) > 0;

                                return (
                                    <div key={b.id}
                                        className="col-12">
                                        <div className="card"
                                            style={{
                                                borderRadius: '12px',
                                                border: `1px solid ${GOLD}`
                                            }}>
                                            <div className="card-body">

                                                {/* Header */}
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <h6 className="fw-bold mb-1"
                                                            style={{
                                                                color: DARK_RED
                                                            }}>
                                                            {b.poojaType}
                                                        </h6>
                                                        <p className="text-muted mb-0"
                                                            style={{
                                                                fontSize: '13px'
                                                            }}>
                                                            📅 {b.poojaDate} at {b.poojaTime}
                                                        </p>
                                                    </div>
                                                    <span className="badge"
                                                        style={{
                                                            backgroundColor: sc.bg,
                                                            color: sc.text
                                                        }}>
                                                        {b.bookingStatus}
                                                    </span>
                                                </div>

                                                {/* User info with rating */}
                                                <div className="p-2 mb-2"
                                                    style={{
                                                        backgroundColor: '#FFF8F8',
                                                        borderRadius: '8px'
                                                    }}>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span style={{
                                                            fontSize: '13px',
                                                            color: DARK_RED,
                                                            fontWeight: 600
                                                        }}>
                                                            👤 {name || 'Devotee'}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '13px'
                                                        }}>
                                                            {rating > 0 ? (
                                                                <span style={{
                                                                    color: rating >= 4
                                                                        ? '#27500A'
                                                                        : rating >= 3
                                                                            ? '#8A5300'
                                                                            : '#791F1F'
                                                                }}>
                                                                    ⭐ {rating}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">
                                                                    New devotee
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* ⚠️ Address Maps mein khulta hai - poojari ko
                                                    aksar naya ghar dhoondhna hota hai, aur pata
                                                    copy karke Maps mein daalna jhanjhat hai.

                                                    ⚠️ encodeURIComponent zaroori hai - pate mein
                                                    comma, space aur kabhi-kabhi # bhi hota hai,
                                                    jo bina encode kiye URL tod dete hain.

                                                    ⚠️ Naye tab mein, rel="noreferrer" ke saath -
                                                    bina iske khula page hamari window ko
                                                    kahin aur bhej sakta hai. */}
                                                <p className="mb-1"
                                                    style={{
                                                        fontSize: '13px'
                                                    }}>
                                                    📍{' '}
                                                    <a
                                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                            [b.poojaAddress, b.poojaCity, b.poojaPincode]
                                                                .filter(Boolean).join(', '))}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-muted">
                                                        {b.poojaAddress}, {b.poojaCity}
                                                        {b.poojaPincode
                                                            && ` - ${b.poojaPincode}`}
                                                    </a>
                                                </p>

                                                {b.specialInstructions && (
                                                    <p className="mb-1"
                                                        style={{
                                                            fontSize: '13px',
                                                            color: '#8A5300'
                                                        }}>
                                                        📝 {b.specialInstructions}
                                                    </p>
                                                )}


                                                {/* ⚠️ SAMPARK - sirf pooja se 24 ghante pehle.
                                                    Us waqt cancel ki khidki band ho chuki hoti
                                                    hai, to sampark ki zaroorat pakki hai:
                                                    poojari ko ghar dhoondhna hota hai.

                                                    ⚠️ Backend tay karta hai ki dikhe ya nahi -
                                                    contactPhone tabhi aata hai. Yahan dobara
                                                    shart nahi lagayi, warna do jagah do niyam
                                                    ho jaate. */}
                                                {b.contactPhone && (
                                                    <a href={`tel:${b.contactPhone}`}
                                                        className="btn btn-sm w-100 fw-bold mt-2"
                                                        style={{
                                                            backgroundColor: '#2E7D32',
                                                            color: 'white',
                                                            borderRadius: '8px',
                                                            fontSize: '13px'
                                                        }}>
                                                        📞 Call {b.contactName || 'devotee'}
                                                    </a>
                                                )}
                                                {b.contactPhone && (
                                                    <div className="text-center text-muted mt-1"
                                                        style={{ fontSize: '11px' }}>
                                                        {b.contactPhone}
                                                    </div>
                                                )}

                                                {b.cancellationReason && (
                                                    <p className="text-danger mb-1"
                                                        style={{
                                                            fontSize: '13px'
                                                        }}>
                                                        ❌ {b.cancellationReason}
                                                    </p>
                                                )}

                                                {/* Payment */}
                                                <div className="p-3 my-3"
                                                    style={{
                                                        backgroundColor: '#FFF5F5',
                                                        borderRadius: '8px'
                                                    }}>
                                                    <div className="d-flex justify-content-between"
                                                        style={{
                                                            fontSize: '13px'
                                                        }}>
                                                        <span className="text-muted">
                                                            Amount
                                                        </span>
                                                        <strong style={{
                                                            color: RED
                                                        }}>
                                                            {formatPaise(b.bookingAmount)}
                                                        </strong>
                                                    </div>
                                                    <div className="d-flex justify-content-between mt-1"
                                                        style={{
                                                            fontSize: '13px'
                                                        }}>
                                                        <span className="text-muted">
                                                            Received
                                                        </span>
                                                        <strong className="text-success">
                                                            {formatPaise(b.paidAmount || 0)}
                                                        </strong>
                                                    </div>
                                                    {unpaid && (
                                                        <div className="d-flex justify-content-between mt-1"
                                                            style={{
                                                                fontSize: '13px'
                                                            }}>
                                                            <span className="text-muted">
                                                                Pending
                                                            </span>
                                                            <strong style={{
                                                                color: '#791F1F'
                                                            }}>
                                                                {formatPaise(b.remainingAmount)}
                                                            </strong>
                                                        </div>
                                                    )}
                                                    {b.samagriIncluded && (
                                                        <div className="d-flex justify-content-between mt-1"
                                                            style={{
                                                                fontSize: '12px'
                                                            }}>
                                                            <span className="text-muted">
                                                                Samagri
                                                            </span>
                                                            <span style={{
                                                                color: '#8A5300'
                                                            }}>
                                                                You provide ·
                                                                {formatPaise(b.samagriPrice)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="d-flex justify-content-between mt-1"
                                                        style={{
                                                            fontSize: '12px'
                                                        }}>
                                                        <span className="text-muted">
                                                            Payment mode
                                                        </span>
                                                        <span className="text-muted">
                                                            {paymentModeLabel(
                                                                b.paymentMode)}
                                                        </span>
                                                    </div>
                                                    {/* ⚠️ Commission breakdown - SIRF POOJARI ko dikhta hai.
                                                        Backend ye fields user ke response mein bhejta hi nahi.

                                                        Ye dikhana ZAROORI hai: bina iske poojari dekhega ki
                                                        ₹400 ki booking thi par ₹360 mila, aur samjhega paisa
                                                        chori ho gaya. Poochne bhi nahi aayega - seedha app
                                                        delete kar dega.

                                                        b.platformFee null hota hai jab tak booking release
                                                        na ho (rate release ke waqt tay hota hai), isliye
                                                        != null check zaroori hai - warna pending bookings
                                                        pe "₹0 fee" dikhega jo galat hai. */}
                                                    {b.platformFee != null && (
                                                        <>
                                                            <hr className="my-2" />
                                                            <div className="d-flex justify-content-between"
                                                                style={{
                                                                    fontSize: '12px'
                                                                }}>
                                                                <span className="text-muted">
                                                                    Platform fee
                                                                    {b.platformFeeBps > 0 &&
                                                                        ` (${b.platformFeeBps / 100}%)`}
                                                                </span>
                                                                <span style={{
                                                                    color: '#8A5300'
                                                                }}>
                                                                    {b.platformFee > 0
                                                                        ? `- ${formatPaise(b.platformFee)}`
                                                                        : 'No fee'}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mt-1"
                                                                style={{
                                                                    fontSize: '13px'
                                                                }}>
                                                                <strong className="text-muted">
                                                                    You earned
                                                                </strong>
                                                                <strong className="text-success">
                                                                    {formatPaise(b.poojariPayout)}
                                                                </strong>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                {/* Actions */}
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {b.bookingStatus
                                                        === 'PENDING' && (
                                                            <>
                                                                <button
                                                                    className="btn btn-sm fw-bold"
                                                                    style={{
                                                                        backgroundColor: RED,
                                                                        color: GOLD
                                                                    }}
                                                                    disabled={busy}
                                                                    onClick={() =>
                                                                        handleConfirm(b.id)}>
                                                                    {busy
                                                                        ? 'Please wait...'
                                                                        : '✅ Accept'}
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    disabled={busy}
                                                                    onClick={() => {
                                                                        setRejectId(b.id);
                                                                        setRejectReason('');
                                                                    }}>
                                                                    Decline
                                                                </button>
                                                            </>
                                                        )}

                                                    {/* ⚠️ serviceDeliveredAt check zaroori hai.
                                                        Agar poojari ne pehle hi "Mark as done"
                                                        dabaya tha par popup mein "nahi mila"
                                                        bola, to booking CONFIRMED hi rehti hai.
                                                        Us case mein ye button nahi, neeche wala
                                                        "Received" button dikhna chahiye. */}
                                                    {b.bookingStatus
                                                        === 'CONFIRMED'
                                                        && !b.serviceDeliveredAt && (
                                                            <button
                                                                className="btn btn-sm fw-bold"
                                                                style={{
                                                                    backgroundColor:
                                                                        '#3B6D11',
                                                                    color: 'white'
                                                                }}
                                                                disabled={busy}
                                                                onClick={() =>
                                                                    handleComplete(b)}>
                                                                {busy
                                                                    ? 'Please wait...'
                                                                    : '🎉 Mark as done'}
                                                            </button>
                                                        )}

                                                    {/* ⚠️ Accept ke BAAD cancel - Decline se alag hai.
                                                        Decline PENDING pe hai aur free hai. Ye
                                                        CONFIRMED pe hai: user ko poora refund jaata
                                                        hai aur POOJARI PAR FEE lagti hai. Isliye
                                                        alag button, alag popup.

                                                        serviceDeliveredAt wali shart zaroori hai -
                                                        pooja ho chuki ho to cancel ka sawaal hi
                                                        nahi. Backend bhi mana karta hai, par
                                                        button hi na dikhe to behtar. */}
                                                    {b.bookingStatus
                                                        === 'CONFIRMED'
                                                        && !b.serviceDeliveredAt && (
                                                            (hoursUntilPooja(b) ?? 0) >= 24 ? (
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    disabled={busy}
                                                                    onClick={() => {
                                                                        setCancelBooking(b);
                                                                        setCancelReason('');
                                                                    }}>
                                                                    Cancel booking
                                                                </button>
                                                            ) : (
                                                                <span className="align-self-center text-muted"
                                                                    style={{ fontSize: '13px' }}>
                                                                    Cannot cancel within 24 hours of the
                                                                    pooja - please call the devotee
                                                                </span>
                                                            )
                                                        )}

                                                    {/* Poojari ne "Mark as done" dabaya tha par
                                                        popup mein "nahi mila" bola. Tab
                                                        serviceDeliveredAt set ho gaya par
                                                        booking CONFIRMED hi rahi. Ab usko
                                                        dobara mauka. */}
                                                    {b.serviceDeliveredAt
                                                        && !b.poojariConfirmedAt
                                                        && unpaid && (
                                                            <button
                                                                className="btn btn-sm fw-bold"
                                                                style={{
                                                                    backgroundColor: RED,
                                                                    color: GOLD
                                                                }}
                                                                onClick={() =>
                                                                    setCashBooking(b)}>
                                                                💵 Received {formatPaise(b.remainingAmount)}
                                                            </button>
                                                        )}

                                                    {b.bookingStatus
                                                        === 'COMPLETED'
                                                        && (reviewed ? (
                                                            <span className="badge align-self-center"
                                                                style={{
                                                                    backgroundColor: '#EAF3DE',
                                                                    color: '#27500A'
                                                                }}>
                                                                ✅ Rated
                                                            </span>
                                                        ) : (
                                                            <button
                                                                className="btn btn-sm fw-bold"
                                                                style={{
                                                                    border: `1px solid ${RED}`,
                                                                    color: RED,
                                                                    backgroundColor:
                                                                        'transparent'
                                                                }}
                                                                onClick={() =>
                                                                    openReviewModal(b)}>
                                                                ⭐ Rate devotee
                                                            </button>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ===== REJECT MODAL ===== */}
            {rejectId && (
                <>
                    <div onClick={() => setRejectId(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%',
                        maxWidth: '420px'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                Decline Booking
                            </div>
                            <div className="card-body p-4">
                                <label className="form-label fw-bold"
                                    style={{ color: DARK_RED }}>
                                    Reason
                                </label>
                                <textarea
                                    className="form-control mb-3"
                                    rows="3"
                                    autoFocus
                                    placeholder="Why can't you take this booking?"
                                    value={rejectReason}
                                    onChange={e =>
                                        setRejectReason(
                                            e.target.value)}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-secondary flex-fill"
                                        onClick={() =>
                                            setRejectId(null)}
                                        disabled={rejecting}>
                                        Close
                                    </button>
                                    <button
                                        className="btn btn-danger flex-fill fw-bold"
                                        onClick={confirmReject}
                                        disabled={rejecting}>
                                        {rejecting
                                            ? 'Declining...'
                                            : 'Confirm'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== POOJARI CANCEL MODAL (accept ke baad) ===== */}
            {/* ⚠️ Decline wale modal se ALAG. Yahan poojari par fee
                lagti hai, isliye chetavni saaf dikhni chahiye - warna
                wo cancel karke baad mein bakaya dekh ke chaunkega. */}
            {cancelBooking && (
                <>
                    <div onClick={() => setCancelBooking(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%',
                        maxWidth: '420px'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                Cancel Confirmed Booking
                            </div>
                            <div className="card-body p-4">
                                <div className="alert alert-warning py-2 px-3 mb-3"
                                    style={{ fontSize: '14px' }}>
                                    <strong>A platform fee will be charged.</strong>
                                    <div className="mt-1">
                                        You accepted this booking, so the devotee
                                        gets a full refund and a platform fee is
                                        added to your pending dues. It will be
                                        adjusted from a future payout.
                                    </div>
                                </div>
                                <label className="form-label fw-bold"
                                    style={{ color: DARK_RED }}>
                                    Reason
                                </label>
                                <textarea
                                    className="form-control mb-3"
                                    rows="3"
                                    autoFocus
                                    placeholder="Why do you need to cancel?"
                                    value={cancelReason}
                                    onChange={e =>
                                        setCancelReason(
                                            e.target.value)}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-secondary flex-fill"
                                        onClick={() =>
                                            setCancelBooking(null)}
                                        disabled={cancelling}>
                                        Keep booking
                                    </button>
                                    <button
                                        className="btn btn-danger flex-fill fw-bold"
                                        onClick={confirmPoojariCancel}
                                        disabled={cancelling}>
                                        {cancelling
                                            ? 'Cancelling...'
                                            : 'Cancel anyway'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== CASH RECEIVED MODAL ===== */}
            {cashBooking && (
                <>
                    <div onClick={() => setCashBooking(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%',
                        maxWidth: '420px'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                Confirm payment
                            </div>
                            <div className="card-body p-4">
                                <p className="mb-3"
                                    style={{
                                        fontSize: '14px',
                                        color: DARK_RED
                                    }}>
                                    Did you receive{' '}
                                    <strong>
                                        {formatPaise(cashBooking.remainingAmount)}
                                    </strong>{' '}
                                    from{' '}
                                    {userNames[cashBooking.userId]
                                        || 'the devotee'}?
                                </p>

                                <div className="p-3 mb-3"
                                    style={{
                                        backgroundColor: '#FFF8F0',
                                        borderRadius: '10px'
                                    }}>
                                    <small className="text-muted">
                                        Only confirm if you have
                                        actually received the money.
                                        This cannot be undone.
                                    </small>
                                </div>

                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-secondary flex-fill"
                                        onClick={() =>
                                            setCashBooking(null)}
                                        disabled={savingCash}>
                                        Not yet
                                    </button>
                                    <button
                                        className="btn flex-fill fw-bold"
                                        style={{
                                            backgroundColor: RED,
                                            color: GOLD
                                        }}
                                        onClick={confirmCash}
                                        disabled={savingCash}>
                                        {savingCash
                                            ? 'Recording...'
                                            : 'Yes, received'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ===== REVIEW MODAL ===== */}
            {reviewBooking && (
                <>
                    <div onClick={() => setReviewBooking(null)}
                        style={{
                            position: 'fixed', top: 0, left: 0,
                            width: '100%', height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}></div>
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060, width: '90%',
                        maxWidth: '460px', maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div className="card"
                            style={{
                                borderRadius: '16px',
                                border: `2px solid ${GOLD}`
                            }}>
                            <div className="card-header fw-bold"
                                style={{
                                    backgroundColor: DARK_RED,
                                    color: GOLD,
                                    borderRadius: '14px 14px 0 0'
                                }}>
                                ⭐ Rate this devotee
                            </div>
                            <div className="card-body p-4">
                                <p className="text-muted mb-3"
                                    style={{ fontSize: '13px' }}>
                                    {userNames[
                                        reviewBooking.userId]
                                        || 'Devotee'} ·{' '}
                                    {reviewBooking.poojaType}
                                </p>

                                <form onSubmit={confirmReview}>

                                    <div className="mb-3 text-center">
                                        <label className="form-label fw-bold d-block"
                                            style={{
                                                color: DARK_RED
                                            }}>
                                            Your Rating
                                        </label>
                                        <div>
                                            {[1, 2, 3, 4, 5].map(
                                                star => (
                                                    <span
                                                        key={star}
                                                        onClick={() =>
                                                            setReviewForm({
                                                                ...reviewForm,
                                                                rating: star
                                                            })}
                                                        style={{
                                                            fontSize: '34px',
                                                            cursor: 'pointer',
                                                            marginRight: '4px',
                                                            filter: star <= reviewForm.rating
                                                                ? 'none'
                                                                : 'grayscale(100%)',
                                                            opacity: star <= reviewForm.rating
                                                                ? 1 : 0.35
                                                        }}>
                                                        ⭐
                                                    </span>
                                                ))}
                                        </div>
                                        <small className="text-muted">
                                            {reviewForm.rating === 5
                                                ? 'Excellent devotee'
                                                : reviewForm.rating === 4
                                                    ? 'Good experience'
                                                    : reviewForm.rating === 3
                                                        ? 'Average'
                                                        : reviewForm.rating === 2
                                                            ? 'Some issues'
                                                            : 'Poor experience'}
                                        </small>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold"
                                            style={{
                                                color: DARK_RED
                                            }}>
                                            Title
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Respectful and well prepared"
                                            value={reviewForm.title}
                                            onChange={e =>
                                                setReviewForm({
                                                    ...reviewForm,
                                                    title: e.target.value
                                                })}
                                            required
                                            style={{
                                                borderColor: GOLD
                                            }}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label fw-bold"
                                            style={{
                                                color: DARK_RED
                                            }}>
                                            Your feedback
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            placeholder="Was the address correct? Was payment on time? Any issues?"
                                            value={reviewForm.comment}
                                            onChange={e =>
                                                setReviewForm({
                                                    ...reviewForm,
                                                    comment: e.target.value
                                                })}
                                            style={{
                                                borderColor: GOLD
                                            }}>
                                        </textarea>
                                        <small className="text-muted">
                                            This helps other poojaris
                                            know what to expect
                                        </small>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-secondary flex-fill"
                                            onClick={() =>
                                                setReviewBooking(null)}
                                            disabled={savingReview}>
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn flex-fill fw-bold"
                                            style={{
                                                backgroundColor: RED,
                                                color: GOLD
                                            }}
                                            disabled={savingReview}>
                                            {savingReview
                                                ? 'Submitting...'
                                                : 'Submit'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PoojariBookings;